import React, { useCallback } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import clsx from 'clsx'
import generateTestId from 'utils/test-helpers'

export interface ToggleSwitchProps {
  active: boolean
  disabled?: boolean
  label?: string
  onClick: (active: boolean) => void
  className?: string
}

export default function ToggleSwitch({
  onClick,
  active = false,
  disabled = false,
  label = '',
  className = '',
}: ToggleSwitchProps) {
  const classes = useStyles()

  const handleClick = useCallback(() => {
    onClick(!active)
  }, [onClick])

  return (
    <div
      data-testid={generateTestId(label, 'toggle')}
      className={clsx(
        classes.toggleSwitch,
        { [classes.toggleSwitchDisabled]: disabled },
        className,
      )}
      onClick={!disabled ? handleClick : undefined}
      role="toggle-switch-control"
    >
      <div
        data-testid={generateTestId('toggle', 'switch')}
        className={classes.switchContainer}
        role="switch"
        aria-checked={active}
      >
        <div
          className={clsx(classes.switchHandle, {
            [classes.switchHandleActive]: active,
            [classes.switchHandleDisabled]: disabled,
          })}
        />
        <div
          className={clsx(classes.switchTrack, {
            [classes.switchTrackActive]: active,
            [classes.switchTrackDisabled]: disabled,
          })}
        />
      </div>
      {!!label && (
        <Text
          className={clsx(classes.switchLabel, { disabled })}
          variant="caption1"
          role="switch-label"
        >
          {label}
        </Text>
      )}
    </div>
  )
}

type ToggleSwitchKeys = keyof Theme['components']['toggleSwitch']
const useStyles = makeStyles<Theme>((theme) => ({
  toggleSwitch: {
    display: 'grid',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'max-content',
    gridAutoFlow: 'column',
    gap: 8,
    cursor: 'pointer',
    margin: '4px 0px',
  },
  toggleSwitchDisabled: {
    cursor: 'not-allowed',
  },
  switchContainer: {
    position: 'relative',
    height: 16,
    display: 'grid',
    alignItems: 'center',
  },
  switchHandle: {
    position: 'absolute',
    borderRadius: 16,
    width: 16,
    height: 16,
    boxShadow: '0 0 12px 0 rgba(13, 13, 40, 0.15)',
    backgroundColor: theme.components.toggleSwitch.inactiveHandle,
    left: 0,
    transition: 'left .2s ease, background-color .2s ease',
  },
  switchHandleActive: {
    backgroundColor: theme.components.toggleSwitch.activeHandle,
    left: 'calc(100% - 16px)',
  },
  switchHandleDisabled: {
    boxShadow: 'unset',
    backgroundColor: theme.components.toggleSwitch.disabledHandle,
  },
  switchTrack: {
    width: 32,
    height: 12,
    borderRadius: 18,
    backgroundColor: theme.components.toggleSwitch.inactiveTrack,
    transition: 'background-color .2s ease',
  },
  switchTrackActive: {
    backgroundColor: theme.components.toggleSwitch.activeTrack,
  },
  switchTrackDisabled: {
    backgroundColor: theme.components.toggleSwitch.disabledTrack,
  },
  switchLabel: {
    color: theme.components.toggleSwitch.label,
    transition: 'color .2s ease',
    '&.disabled': {
      color: theme.components.toggleSwitch.disabledLabel,
    },
    '&:not(.disabled):hover': {
      color: theme.components.toggleSwitch.hoverLabel,
    },
  },
}))
